import { useEffect, useRef, useState, type ReactNode } from 'react';

import { ReplyDraftTourTooltip } from './ReplyDraftTourTooltip';
import { createReplyDraftTourActions, type RevealTarget } from './replyDraftTourActions';
import {
  ReplyDraftTourContext,
  useReplyDraftTourActions,
  useReplyDraftTourStore,
  useReplyDraftTourStoreApi,
} from './replyDraftTourContext';
import type { DraftStep } from '../../model/step';
import { REPLY_DRAFT_TOUR_SEGMENTS } from '../../model/tour';
import {
  createReplyDraftTourStore,
  selectIsTourLive,
  selectReadySegment,
} from '../../state/replyDraftTourStore';
import { REPLY_DRAFT_COPY } from '../copy';

import { SpotlightTourProvider, useSpotlightTour } from '@/components/spotlight/SpotlightTour';
import { Button, ButtonText } from '@/components/ui/button';
import { View } from '@/components/ui/view';
import { DURATION } from '@/constants/animation';
import { useColors } from '@/hooks/useColors';
import { useTourCallout } from '@/hooks/useTourCallout';
import { CALLOUT_KEYS } from '@/services/userCallouts';
import { getAccessibilityProps } from '@/util/accessibilityProps';

/**
 * How long a segment waits after its first target mounts before it shows.
 * Long enough for the wizard's presentation, the web modal's scale-in among
 * it, and a step change's scroll to settle.
 */
const START_DELAY = DURATION.slow + DURATION.fast;

/**
 * Shows the segment the tour is waiting on once its first target has mounted,
 * and takes a segment off the screen when the tour moves on without it or its
 * first target goes away, as it does when the expert picks a position
 * mid-tour. Scrolls the segment's first target into sight before it shows.
 */
function ReplyDraftTourDriver() {
  const store = useReplyDraftTourStoreApi();

  const { segmentShown, reveal } = useReplyDraftTourActions();

  const phase = useReplyDraftTourStore((state) => state.phase);

  const running = useReplyDraftTourStore((state) => state.running);

  const readySegment = useReplyDraftTourStore(selectReadySegment);

  const decide = useSpotlightTour(REPLY_DRAFT_TOUR_SEGMENTS.decide.tourKey);

  const review = useSpotlightTour(REPLY_DRAFT_TOUR_SEGMENTS.review.tourKey);

  const controllersRef = useRef({ decide, review });

  useEffect(() => {
    controllersRef.current = { decide, review };
  });

  useEffect(() => {
    if (running && (running !== phase || running !== readySegment)) {
      controllersRef.current[running].stop();

      store.getState().segmentInterrupted(running);
    }
  }, [running, phase, readySegment, store]);

  useEffect(() => {
    if (!readySegment || running === readySegment) {
      return;
    }

    let isCancelled = false;

    const timer = setTimeout(() => {
      reveal(readySegment, 1)
        .catch(() => undefined)
        .finally(() => {
          if (isCancelled) {
            return;
          }

          if (controllersRef.current[readySegment].start()) {
            segmentShown(readySegment);
          }
        });
    }, START_DELAY);

    return () => {
      isCancelled = true;

      clearTimeout(timer);
    };
  }, [readySegment, running, segmentShown, reveal]);

  return null;
}

type ReplyDraftTourProviderProps = {
  step: DraftStep;
  /** Holds the tour back while something covers the draft, such as an open source. */
  isPaused: boolean;
  /** Scrolls the wizard's body so a target is in sight; the tour itself never scrolls. */
  onRevealTarget: RevealTarget;
  replyDraftId: string;
  conversationId: string;
  children: ReactNode;
};

/**
 * Hosts the reply draft tour for one open wizard, on the phone's full-screen
 * route and in the web modal alike.
 *
 * It starts on an expert's first draft, gated by its callout, once the draft
 * has loaded and the wizard knows its step. Nothing else gates it: the wizard
 * only opens for an expert allowed to draft.
 *
 * The spotlight dims the whole window, the native header and the web modal's
 * surroundings included. Its card may render outside this tree, so it carries
 * the tour's context with it.
 *
 * Dark mode dims with black at a heavier alpha. The light theme's scrim is
 * itself a near-black, and over an already dark screen it barely darkens, so
 * the highlighted target stops standing out from what surrounds it.
 */
export function ReplyDraftTourProvider({
  step,
  isPaused,
  onRevealTarget,
  replyDraftId,
  conversationId,
  children,
}: ReplyDraftTourProviderProps) {
  const { colors, isDarkScheme } = useColors();

  const callout = useTourCallout({ key: CALLOUT_KEYS.REPLY_DRAFT_INTRO, enabled: true });

  const calloutRef = useRef(callout);

  const idsRef = useRef({ replyDraftId, conversationId });

  const revealRef = useRef<RevealTarget | undefined>(onRevealTarget);

  const [tour] = useState(() => {
    const store = createReplyDraftTourStore();

    return { store, actions: createReplyDraftTourActions(store, calloutRef, idsRef, revealRef) };
  });

  useEffect(() => {
    calloutRef.current = callout;

    idsRef.current = { replyDraftId, conversationId };

    revealRef.current = onRevealTarget;
  });

  useEffect(() => {
    if (step === 'loading') {
      return;
    }

    tour.store.getState().stepChanged(step);

    if (callout.shouldShow) {
      tour.actions.start(step);
    }
  }, [step, callout.shouldShow, tour]);

  useEffect(() => {
    tour.store.getState().setPaused(isPaused);
  }, [isPaused, tour]);

  return (
    <View className="h-full w-full flex-1">
      <ReplyDraftTourContext.Provider value={tour}>
        <SpotlightTourProvider
          renderCard={(spotlightStep) => (
            <ReplyDraftTourContext.Provider value={tour}>
              <ReplyDraftTourTooltip step={spotlightStep} />
            </ReplyDraftTourContext.Provider>
          )}
          backdropColor={isDarkScheme ? `${colors.black}CC` : `${colors.background.dark}99`}>
          <ReplyDraftTourDriver />

          {children}
        </SpotlightTourProvider>
      </ReplyDraftTourContext.Provider>
    </View>
  );
}

/**
 * Replays the tour from the step the expert is on. Hidden while the tour runs,
 * since it would start the tour it sits under, and while `isOffered` is off,
 * as it is while must-fix items stand in for the points: the points segment
 * could never start there, and the button would vanish with nothing shown.
 */
export function ReplyDraftTourReplay({ step, isOffered }: { step: DraftStep; isOffered: boolean }) {
  const { replay } = useReplyDraftTourActions();

  const isLive = useReplyDraftTourStore(selectIsTourLive);

  if (isLive || !isOffered || step === 'loading') {
    return null;
  }

  return (
    <Button
      variant="link"
      action="secondary"
      className="h-12 self-start px-0"
      onPress={() => replay(step)}
      testID="reply-draft-tour-replay"
      {...getAccessibilityProps({
        role: 'button',
        label: REPLY_DRAFT_COPY.tour.replay,
        hint: REPLY_DRAFT_COPY.tour.replayHint,
      })}>
      <ButtonText className="text-secondary-700">{REPLY_DRAFT_COPY.tour.replay}</ButtonText>
    </Button>
  );
}
